<template>
  <div class="container-xl px-4">
    <div class="row">
      <div class="col-12">
        <div class="d-flex justify-content-between align-items-center mb-4 mt-2">
          <div>
            <h2 class="fw-bold mb-1">Mantenimiento Operativo</h2>
            <p class="text-muted mb-0">
              Configuración operativa de la sucursal {{ currentSucursalId }}
            </p>
          </div>
          <button class="btn btn-outline-secondary" :disabled="loading" @click="loadData">
            <i class="bi bi-arrow-clockwise me-2"></i>Recargar
          </button>
        </div>
      </div>
    </div>

    <div class="row g-4">
      <div class="col-12 col-xl-7">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="fw-bold">Parámetros por sucursal</h5>
            <small class="text-muted d-block mb-3">
              Esta configuración se aplica inmediatamente a nuevas operaciones de inventario.
            </small>

            <div class="mb-3">
              <label class="form-label">Modo de operación</label>
              <select class="form-select" v-model="form.modo_operacion_sucursal">
                <option value="MULTIDEPOSITO_MANUAL">Multidepósito manual</option>
                <option value="MONODEPOSITO_AUTO_MOSTRADOR">Monodepósito auto a mostrador</option>
                <option value="MULTIDEPOSITO_DEPOSITO_DEFAULT">Multidepósito con depósito default</option>
              </select>
              <small class="text-muted">
                Define si la sucursal resuelve automáticamente el depósito destino o si exige selección manual.
              </small>
            </div>

            <div class="mb-3">
              <label class="form-label">Depósito mostrador</label>
              <select class="form-select" v-model.number="form.deposito_mostrador_id">
                <option :value="null">Sin definir</option>
                <option v-for="dep in operationalWarehouses" :key="'mostrador_'+dep.id" :value="dep.id">
                  {{ dep.nombre }}
                </option>
              </select>
            </div>

            <div class="row g-3">
              <div class="col-12 col-md-6">
                <label class="form-label">Depósito default entrada</label>
                <select class="form-select" v-model.number="form.deposito_default_entrada">
                  <option :value="null">Sin definir</option>
                  <option v-for="dep in operationalWarehouses" :key="'entrada_'+dep.id" :value="dep.id">
                    {{ dep.nombre }}
                  </option>
                </select>
              </div>

              <div class="col-12 col-md-6">
                <label class="form-label">Depósito default recepción</label>
                <select class="form-select" v-model.number="form.deposito_default_recepcion">
                  <option :value="null">Sin definir</option>
                  <option v-for="dep in operationalWarehouses" :key="'recepcion_'+dep.id" :value="dep.id">
                    {{ dep.nombre }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-check form-switch mt-4">
              <input
                id="autoMostrador"
                v-model="form.auto_transferir_a_mostrador"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label" for="autoMostrador">
                Auto transferir a mostrador cuando la operación lo requiera
              </label>
            </div>

            <div class="form-check form-switch mt-2">
              <input
                id="soloMostrador"
                v-model="form.consumo_venta_solo_mostrador"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label" for="soloMostrador">
                Consumir ventas sólo desde mostrador
              </label>
            </div>

            <hr class="my-4" />

            <h5 class="fw-bold">Operación de caja</h5>
            <small class="text-muted d-block mb-3">
              Cómo opera el puesto de ventas de esta sucursal. Se guarda con el mismo botón de arriba.
            </small>

            <div class="form-check form-switch">
              <input
                id="modoCajeroUnico"
                v-model="form.modo_cajero_unico"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label" for="modoCajeroUnico">
                Modo cajero único
              </label>
              <small class="text-muted d-block">
                El mismo operador carga el pedido y lo cobra, sin pasar por la pantalla de cobros
                pendientes. Requiere permiso de cobro y caja abierta. Si el usuario no puede cobrar,
                la pantalla de ventas funciona igual que siempre.
              </small>
            </div>

            <!-- P2 (SPEC_VENTAS_INNOMINADAS_FACTURACION_DIFERIDA_v0.1.md RN-VI2). -->
            <div class="form-check form-switch mt-3">
              <input
                id="ventasInnominadasTicket"
                v-model="form.ventas_innominadas_ticket_habilitado"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label" for="ventasInnominadasTicket">
                Cobrar con ticket las ventas sin documento
              </label>
              <small class="text-muted d-block">
                Al cobrar una venta a «Cliente sin nombre», el cajero puede elegir ticket en lugar de
                factura electrónica. La venta se registra completa (stock y caja) y queda pendiente de
                factura, para facturarla en el sistema externo desde Mantenimiento › Fiscal › Ventas
                pendientes de factura. Sin notas de crédito; se anula sólo dentro de las 24 horas.
              </small>
            </div>

            <!-- SPEC_CARGA_INICIAL_STOCK_Y_PRECIOS_v0.1.md RH-1/RH-2. -->
            <div class="form-check form-switch mt-3">
              <input
                id="cargaInicialStockHabilitada"
                v-model="form.carga_inicial_stock_habilitada"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label" for="cargaInicialStockHabilitada">
                Carga inicial de stock
              </label>
              <small class="text-muted d-block">
                Habilita Compras › Carga inicial de stock: la mercadería que ya está en el local se
                carga como una compra (sin timbrado ni número, que se completan solos; el costo puede
                quedar en 0) y, en la misma pantalla, su precio de venta. Para el alta del comercio:
                apagalo cuando termines.
              </small>
            </div>

            <div class="form-check form-switch mt-3">
              <input
                id="atajosTecladoHabilitados"
                v-model="form.atajos_teclado_habilitados"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label" for="atajosTecladoHabilitados">
                Atajos de teclado en Pantalla de Inicio
              </label>
              <small class="text-muted d-block">
                Habilita F2 (buscar producto), F4 (dejar en espera), F8 (cancelar) y F9
                (confirmar/cobrar). Desactivalo si algún periférico interfiere con estas teclas.
              </small>
            </div>

            <div class="form-check form-switch mt-3">
              <input
                id="mostrarHintsAtajos"
                v-model="form.mostrar_hints_atajos"
                class="form-check-input"
                type="checkbox"
                :disabled="!form.atajos_teclado_habilitados"
              />
              <label class="form-check-label" for="mostrarHintsAtajos">
                Mostrar barra de atajos en pantalla
              </label>
            </div>

            <div v-if="validationMessage" class="alert alert-warning mt-4 mb-0">
              {{ validationMessage }}
            </div>

            <div class="d-flex gap-2 mt-4">
              <button class="btn btn-dark" :disabled="saving || !canSave" @click="save">
                <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Guardar configuración
              </button>
              <button class="btn btn-outline-secondary" :disabled="saving" @click="resetForm">
                Revertir cambios
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-xl-5">
        <div class="card mb-4">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start gap-3 mb-3">
              <div>
                <h5 class="fw-bold mb-1">Facturación electrónica</h5>
                <small class="text-muted">
                  La configuración efectiva vive en Mantenimiento > Fiscal para mantener emisor, timbrado y serie alineados.
                </small>
              </div>
            </div>
            <router-link class="btn btn-dark btn-sm" :to="{ name: 'MaintenanceFiscalDashboard' }">
              Abrir perfil fiscal
            </router-link>
          </div>
        </div>

        <div class="card mb-4">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start gap-3 mb-3">
              <div>
                <h5 class="fw-bold mb-1">Impresión local POS</h5>
                <small class="text-muted">
                  Define si la factura se imprime directamente o si requiere confirmación antes de enviarse al cliente local.
                </small>
              </div>
              <button class="btn btn-outline-secondary btn-sm" :disabled="printAgentLoading" @click="refreshPrintAgentData">
                <i class="bi bi-arrow-clockwise me-1"></i>Actualizar
              </button>
            </div>

            <div class="alert mb-3" :class="printAgentOnline ? 'alert-success' : 'alert-warning'">
              <div class="fw-semibold">
                {{ printAgentOnline ? 'Cliente local detectado' : 'Cliente local no disponible' }}
              </div>
              <small>{{ printAgentStatusMessage }}</small>
            </div>

            <div class="mb-3">
              <label class="form-label">Modo de impresión de factura</label>
              <select v-model="localPrintForm.invoicePrintMode" class="form-select">
                <option value="auto">Imprimir directamente</option>
                <option value="confirm">Solicitar confirmación antes de imprimir</option>
                <option value="disabled">No imprimir automáticamente</option>
              </select>
            </div>

            <div class="row g-3">
              <div class="col-12">
                <label class="form-label">Impresora local</label>
                <select v-model="localPrintForm.printerName" class="form-select">
                  <option value="">Usar impresora por defecto del agente</option>
                  <option v-for="printer in localPrinters" :key="printer.name" :value="printer.name">
                    {{ printer.name }}{{ printer.isDefault ? ' (default)' : '' }}
                  </option>
                </select>
              </div>

              <div class="col-6">
                <label class="form-label">Backend</label>
                <select v-model="localPrintForm.backend" class="form-select">
                  <option value="raw">raw</option>
                  <option value="tcp9100">tcp9100</option>
                </select>
              </div>

              <div class="col-6">
                <label class="form-label">Copias</label>
                <input v-model.number="localPrintForm.copies" class="form-control" type="number" min="1" max="20" />
              </div>
            </div>

            <div class="form-check form-switch mt-3">
              <input
                id="localPrintCut"
                v-model="localPrintForm.cut"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label" for="localPrintCut">
                Agregar corte al final del ticket
              </label>
            </div>

            <div class="mt-3">
              <label class="form-label">API key del agente local</label>
              <input
                v-model="localPrintForm.apiKey"
                class="form-control"
                type="text"
                placeholder="Opcional"
              />
              <small class="text-muted">
                Solo se envía si tu instalación local del agente exige `X-Api-Key`.
              </small>
            </div>

            <div class="d-flex gap-2 mt-4">
              <button class="btn btn-dark" @click="saveLocalPrintSettings">
                Guardar impresión local
              </button>
              <button class="btn btn-outline-secondary" @click="resetLocalPrintSettings">
                Revertir
              </button>
            </div>
          </div>
        </div>

        <div v-if="canManageScaleFormats" class="card mb-4">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 class="fw-bold mb-1">Balanzas</h5>
                <small class="text-muted">La administración de formatos de balanza ahora vive en una subsección propia del módulo Mantenimiento.</small>
              </div>
              <router-link class="btn btn-dark btn-sm" :to="{ name: 'MaintenanceScales' }">
                Abrir Balanzas
              </router-link>
            </div>
            <p class="text-muted mb-3">
              Se mantiene dentro de `Mantenimiento` pero separado de la configuración operativa y fiscal para mejorar escalabilidad.
            </p>
            <div class="d-flex gap-2 flex-wrap">
              <router-link class="btn btn-outline-dark btn-sm" :to="{ name: 'MaintenanceScales' }">
                Gestionar formatos
              </router-link>
              <router-link class="btn btn-link btn-sm px-0" :to="{ name: 'MaintenanceScales' }">
                Ver listado completo
              </router-link>
            </div>
          </div>
        </div>

        <div class="card mb-4">
          <div class="card-body">
            <h5 class="fw-bold">Resumen operativo</h5>
            <small class="text-muted d-block mb-3">
              Lectura derivada de la configuración y de los depósitos activos/habilitados.
            </small>

            <div class="summary-grid">
              <div class="summary-item">
                <span class="text-muted">Depósitos operativos</span>
                <strong>{{ summary.activeEnabled.length }}</strong>
              </div>
              <div class="summary-item">
                <span class="text-muted">Mostrador</span>
                <strong>{{ summary.counterWarehouse?.nombre || 'Sin definir' }}</strong>
              </div>
              <div class="summary-item">
                <span class="text-muted">Resolución automática</span>
                <strong>{{ summary.canAutoUseCounter ? 'Sí' : 'No' }}</strong>
              </div>
              <div class="summary-item">
                <span class="text-muted">Modo</span>
                <strong>{{ modeLabel(summary.config.modo_operacion_sucursal) }}</strong>
              </div>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-body">
            <h5 class="fw-bold">Depósitos de la sucursal</h5>
            <small class="text-muted d-block mb-3">
              Sólo se listan depósitos de la sucursal actual.
            </small>

            <div v-if="loading" class="text-muted">Cargando depósitos...</div>
            <div v-else-if="!warehouses.length" class="text-muted">No hay depósitos registrados.</div>
            <div v-else class="list-group list-group-flush">
              <div
                v-for="dep in warehouses"
                :key="dep.id"
                class="list-group-item d-flex justify-content-between align-items-center gap-3"
              >
                <div>
                  <div class="fw-semibold">{{ dep.nombre }}</div>
                  <small class="text-muted">
                    {{ dep.activo ? 'Activo' : 'Inactivo' }} / {{ dep.habilitado ? 'Habilitado' : 'No habilitado' }}
                  </small>
                </div>
                <div class="d-flex gap-2 flex-wrap justify-content-end">
                  <span v-if="dep.id === form.deposito_mostrador_id" class="badge bg-dark">Mostrador</span>
                  <span v-if="dep.id === form.deposito_default_entrada" class="badge bg-secondary">Default entrada</span>
                  <span v-if="dep.id === form.deposito_default_recepcion" class="badge bg-secondary">Default recepción</span>
                </div>
              </div>
            </div>

            <router-link class="btn btn-link px-0 mt-3" :to="{ name: 'InvDepositos' }">
              Ir a gestión de depósitos
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import apiService from '@/services/apiService'
import operationalConfigService from '@/services/operationalConfigService'
import localPrintSettingsService from '@/services/localPrintSettingsService'
import printAgentService from '@/services/printAgentService'

const currentSucursalId = operationalConfigService.getCurrentSucursalId()
const loading = ref(false)
const saving = ref(false)
const warehouses = ref([])
const localPrinters = ref([])
const printAgentLoading = ref(false)
const printAgentOnline = ref(false)
const printAgentStatusMessage = ref('Sin verificar')

const form = reactive({
  ...operationalConfigService.getConfig(currentSucursalId)
})

const localPrintForm = reactive({
  ...localPrintSettingsService.getSettings()
})

function normalizeWarehouse(raw) {
  return {
    id: raw.deposito_id,
    sucursalId: raw.sucursal_id,
    nombre: raw.nombre,
    descripcion: raw.descripcion,
    activo: !!raw.activo,
    habilitado: !!raw.habilitado
  }
}

const operationalWarehouses = computed(() =>
  warehouses.value.filter(dep => dep.activo && dep.habilitado)
)

const summary = computed(() =>
  operationalConfigService.getOperationalSummary({
    sucursalId: currentSucursalId,
    warehouses: warehouses.value,
    config: form
  })
)

const validationMessage = computed(() => {
  if (form.consumo_venta_solo_mostrador && !form.deposito_mostrador_id) {
    return 'Debe definir un depósito mostrador si la venta consume sólo desde mostrador.'
  }

  if (
    form.modo_operacion_sucursal === 'MONODEPOSITO_AUTO_MOSTRADOR' &&
    !summary.value.canAutoUseCounter
  ) {
    return 'Este modo requiere exactamente un depósito activo/habilitado y que coincida con el mostrador configurado.'
  }

  if (
    form.modo_operacion_sucursal === 'MULTIDEPOSITO_DEPOSITO_DEFAULT' &&
    !form.deposito_default_entrada &&
    !form.deposito_default_recepcion
  ) {
    return 'Defina al menos un depósito default para entrada o recepción.'
  }

  return ''
})

const canSave = computed(() => !validationMessage.value)
const currentRoleName = computed(() => String(localStorage.getItem('rol_name') || '').trim().toLowerCase())
const canManageScaleFormats = computed(() => ['administrador', 'admin', 'gerente'].includes(currentRoleName.value))

function modeLabel(mode) {
  if (mode === 'MONODEPOSITO_AUTO_MOSTRADOR') return 'Monodepósito auto mostrador'
  if (mode === 'MULTIDEPOSITO_DEPOSITO_DEFAULT') return 'Multidepósito con default'
  return 'Multidepósito manual'
}

async function loadData() {
  loading.value = true
  try {
    const { data } = await apiService.get('/api/inventory/warehouses')
    const rows = Array.isArray(data?.depositos) ? data.depositos : []
    warehouses.value = rows
      .map(normalizeWarehouse)
      .filter(dep => Number(dep.sucursalId) === Number(currentSucursalId))
  } catch (error) {
    console.error('Error cargando depósitos para mantenimiento operativo', error)
    warehouses.value = []
  } finally {
    loading.value = false
  }
}

function resetForm() {
  Object.assign(form, operationalConfigService.getConfig(currentSucursalId))
}

async function save() {
  if (!canSave.value) return
  saving.value = true
  try {
    // FH-5 (A11): se guarda en el servidor; "guardada" sólo si respondió bien.
    const nextConfig = await operationalConfigService.saveConfig(currentSucursalId, form)
    Object.assign(form, nextConfig)
    window.dispatchEvent(new CustomEvent('app:operational-config-changed', {
      detail: { sucursal_id: currentSucursalId }
    }))
    window.alert('Configuración operativa guardada')
  } catch (error) {
    const detalle = error?.response?.data?.detalle || error?.response?.data?.error || error?.message
    window.alert(`No se guardó la configuración operativa: ${detalle || 'error desconocido'}`)
  } finally {
    saving.value = false
  }
}

async function refreshPrintAgentData() {
  printAgentLoading.value = true
  try {
    const [health, printers] = await Promise.all([
      printAgentService.getHealth(),
      printAgentService.getPrinters()
    ])
    printAgentOnline.value = Boolean(health?.ok)
    printAgentStatusMessage.value = health?.version
      ? `Agente activo. Version ${health.version}.`
      : 'Agente activo.'
    localPrinters.value = Array.isArray(printers) ? printers : []
  } catch (error) {
    printAgentOnline.value = false
    printAgentStatusMessage.value = error?.message || 'No se pudo conectar con el cliente local.'
    localPrinters.value = []
  } finally {
    printAgentLoading.value = false
  }
}

function saveLocalPrintSettings() {
  const saved = localPrintSettingsService.saveSettings(localPrintForm)
  Object.assign(localPrintForm, saved)
  window.alert.success('Configuración de impresión local guardada')
}

function resetLocalPrintSettings() {
  Object.assign(localPrintForm, localPrintSettingsService.getSettings())
}

onMounted(async () => {
  // FH-5 (A11): el formulario muestra lo que tiene el servidor, no la caché.
  Object.assign(form, await operationalConfigService.loadConfig(currentSucursalId))
  await loadData()
  await refreshPrintAgentData()
})
</script>

<style scoped>
.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  background: #fafafa;
}

@media (max-width: 768px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
